import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import DatePicker from '@components/DatePicker';
import moment, { Moment } from 'moment';
import ActionTypes from '@utils/deviceActions';
import {
  Button,
  Descriptions,
  Divider,
  Form,
  FormInstance,
  Popover,
  Radio,
  Tag,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { IDevice } from '@pages/Assets/Inventory/types';

interface NetworkBlockPayloadProps {
  action: ActionTypes.NETWORK_BLOCK;
  devices: {
    msisdn: string;
    processData: string;
    // Permanent | processData: '{"tpBloqueio":1,"dtInicioBloqueio":"20240119"}';
    // Renewal | processData: '{"tpBloqueio":2,"dtInicioBloqueio":"20240119"}';
    // Temporary | processData: '{"tpBloqueio":3,"dtInicioBloqueio":"20240119","dtFimBloqueio":"20240129"}';
  }[];
}

interface NetworkBlockProps {
  devices: IDevice[];
  form: FormInstance<any>;
  isUpload?: boolean;
}

const useNetworkBlock = ({
  devices,
  isUpload,
  form,
}: NetworkBlockProps): DeviceActionProps => {
  const { t } = useTranslation(['actions-modal', 'attributes']);
  const blockType = Form.useWatch('blockType', form);
  const blockDate = Form.useWatch('blockDate', form);

  const isBlockTemporary = blockType === 3;

  const blockTypes = useMemo(
    () => [
      {
        label: t('network-block.permanent'),
        value: 1,
        content: t('network-block.permanent-description'),
      },
      {
        label: t('network-block.renewal'),
        value: 2,
        content: t('network-block.renewal-description'),
      },
      {
        label: t('network-block.temporary'),
        value: 3,
        content: t('network-block.temporary-description'),
      },
    ],
    [t],
  );

  const blockTypeHelp = useMemo(
    () => (
      <Descriptions
        layout="vertical"
        size="small"
        column={1}
        colon={false}
        style={{ maxWidth: !isUpload ? 280 : 'auto' }}
      >
        {blockTypes.map(({ label, value, content }) => (
          <Descriptions.Item
            key={label}
            label={
              isUpload ? (
                <div>
                  <Tag>{value}</Tag>
                  {label}
                </div>
              ) : (
                label
              )
            }
            contentStyle={{ marginBottom: 14 }}
          >
            {content}
          </Descriptions.Item>
        ))}
      </Descriptions>
    ),
    [blockTypes, isUpload],
  );

  const disabledDate = useCallback((currentDate: Moment) => {
    const today = moment().startOf('day');

    return currentDate && currentDate.startOf('day') < today;
  }, []);

  const handleBlockTypeChange = useCallback(
    (type: 1 | 2 | 3) => {
      const isTemporary = type === 3;
      let blockValue;
      if (!isTemporary) {
        blockValue = moment();
      }
      form?.setFieldValue('blockDate', blockValue);
    },
    [form],
  );

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: (
          <>
            {t('attributes:blockType')}
            <Popover content={blockTypeHelp}>
              <Button
                type="link"
                size="small"
                style={{ marginLeft: 4 }}
                icon={<QuestionCircleOutlined />}
              />
            </Popover>
          </>
        ),
        name: 'blockType',
        content: (
          <Radio.Group
            optionType="button"
            options={blockTypes}
            onChange={({ target: { value } }) => handleBlockTypeChange(value)}
          />
        ),
      },
      {
        title: t('attributes:blockDate'),
        name: 'blockDate',
        content: (
          <DatePicker
            disabledDate={disabledDate}
            range={isBlockTemporary}
            style={{ minWidth: '214px' }}
          />
        ),
      },
    ],
    [
      blockTypeHelp,
      blockTypes,
      disabledDate,
      handleBlockTypeChange,
      isBlockTemporary,
      t,
    ],
  );

  const formatBlockDate = useCallback(
    (date: Moment) => date?.format('YYYYMMDD'),
    [],
  );

  const blockDates = useMemo(() => {
    if (!blockDate) {
      return undefined;
    }
    const startDate = isBlockTemporary ? blockDate?.[0] : blockDate;

    const endDate = isBlockTemporary ? blockDate?.[1] : undefined;

    return {
      startDate: formatBlockDate(startDate),
      endDate: formatBlockDate(endDate),
    };
  }, [blockDate, isBlockTemporary, formatBlockDate]);

  const payloadToSend = useMemo(
    (): NetworkBlockPayloadProps => ({
      action: ActionTypes.NETWORK_BLOCK,
      devices: devices?.map(device => ({
        msisdn: device.msisdn,
        processData: JSON.stringify({
          tpBloqueio: isUpload ? device.blockingType : blockType,
          dtInicioBloqueio: isUpload
            ? device.blockingInitialDate
            : blockDates?.startDate,
          dtFimBloqueio: isUpload
            ? device.blockingFinalDate
            : blockDates?.endDate,
        }),
      })),
    }),
    [blockDates?.endDate, blockDates?.startDate, blockType, devices, isUpload],
  );

  const uploadCustomInfo = useMemo(
    () => (
      <div style={{ opacity: 0.9 }}>
        <Divider style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: 0 }}>
          {t('attributes:blockType')}
        </Divider>
        {blockTypeHelp}
      </div>
    ),
    [blockTypeHelp, t],
  );

  return {
    steps,
    uploadCustomInfo,
    infoMessage: t('network-block.info'),
    payloadToSend,
  };
};

export default useNetworkBlock;
